// NotificationBell — extracted from Layout.js (V28 refactor).
// Polls /api/notifications every 30s. Self-contained: state, fetching, UI.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, Phone, CreditCard, Clock, DollarSign as DollarIcon } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(`${API_URL}/api/notifications/clear-all`, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'callback_reminder':
        return <Phone className="w-4 h-4 text-yellow-600" />;
      case 'payment_received':
        return <DollarIcon className="w-4 h-4 text-green-600" />;
      case 'payment_created':
        return <CreditCard className="w-4 h-4 text-blue-600" />;
      case 'renewal_due':
        return <Clock className="w-4 h-4 text-orange-600" />;
      default:
        return <Bell className="w-4 h-4 text-slate-600" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-[400px] overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-indigo-600 hover:text-indigo-700"
                    data-testid="notification-mark-all-read-btn"
                  >
                    Mark read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearAllNotifications}
                    className="text-xs text-red-600 hover:text-red-700"
                    data-testid="notification-clear-all-btn"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-[320px]">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  No notifications
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.notification_id}
                    className={`p-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${
                      !notif.is_read ? 'bg-indigo-50/50' : ''
                    }`}
                    onClick={() => !notif.is_read && markAsRead(notif.notification_id)}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{getNotificationIcon(notif.notification_type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notif.is_read ? 'font-semibold' : 'font-medium'} text-slate-900`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                      {!notif.is_read && <div className="w-2 h-2 bg-indigo-600 rounded-full mt-1.5" />}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
